import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { PackageX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { message } from './PackToolShell';

const CheckRow = ({ row, onDone }) => {
  const [count, setCount] = useState(''), [reason, setReason] = useState(''), [busy, setBusy] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());
  const left = Number(count) > 0;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/opened-packs/${row.pack_id}/verify-empty`, { request_id: requestId, expected_version: row.stock_version, counted: Number(count), reason });
      toast.success(data.status === 'inactive' ? `${row.pack_id} confirmed empty — code inactivated` : `Count of ${count} sent for approval — ${row.pack_id} stays active`);
      onDone();
    } catch (e) { toast.error(message(e)); onDone(); } finally { setBusy(false); }
  };
  return <div className="pack-empty-row" data-testid={`empty-check-${row.pack_id}`}>
    <div><strong>{row.pack_id}</strong> · {row.name}<small className="block">Batch {row.batch_number} · Exp {row.expiry_date?.slice(0, 10)} · Shelf {row.shelf || 'Unassigned'}</small></div>
    {row.count_pending ? <span className="pack-note" data-testid={`empty-check-pending-${row.pack_id}`}>Count awaiting approval</span> : <div className="pack-empty-form">
      <Input type="number" min="0" step="1" value={count} onChange={e => setCount(e.target.value)} placeholder="Units physically in pack" aria-label="Units physically counted" data-testid={`empty-check-count-${row.pack_id}`} />
      {left && <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why units remain" maxLength={500} data-testid={`empty-check-reason-${row.pack_id}`} />}
      <Button size="sm" disabled={busy || count === '' || (left && reason.trim().length < 3)} onClick={submit} data-testid={`empty-check-submit-${row.pack_id}`}>{left ? 'Send count for approval' : 'Confirm empty & inactivate'}</Button>
    </div>}
  </div>;
};

export const EmptyPackChecks = ({ canManage, onChange }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get('/opened-packs/empty-checks').then(r => setItems(r.data.items)).catch(() => setItems([])), []);
  const done = () => { load(); onChange?.(); };
  useEffect(() => { load(); }, [load]);
  if (!items?.length) return null;
  return <section className="pack-empty" data-testid="empty-pack-checks">
    <h2 className="flex items-center gap-2"><PackageX size={18} /> Zero-balance packs to check ({items.length})</h2>
    <p className="pack-note">These packs read zero. Count each one by hand: confirm empty to inactivate its code, or record what is left for a manager to approve.</p>
    {canManage ? items.map(row => <CheckRow key={row.pack_id} row={row} onDone={done} />) : <p className="pack-note">Ask a stock manager to complete these checks.</p>}
  </section>;
};
